import client from "api/WookAxios";
import ConsentModal from "auth/register/ConsentModal";
import RegisterModal from "auth/register/RegisterModal";
import OAuthCompleteModal from "auth/register/OAuthCompleteModal";
import RegisterCheckModal from "auth/register/RegisterCheckModal";
import ModalContext from "components/contexts/ModalContext";
import MenuContext from "header/MenuContext";
import log from "loglevel";
import { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { REGISTER_PAGE } from "auth/register/constants";
import { useAuth } from "auth/useAuth";
import { parseErrors, parseFieldError } from "lib/errors/parse";
import { handleThrottle } from "lib/errors/handle";

export const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { syncAuth } = useAuth();
  
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState({});
  const [oauthContext, setOAuthContext] = useState(null);
  const [registerData, setRegisterData] = useState({
    username: '',
    password: '',
    password2: '',
    name: '',
    email: '',
    mobile: '',
  });
  
  const isOAuth = searchParams.get('context') === 'oauth';
  const [pageState, setPageState] = useState(
    isOAuth ? REGISTER_PAGE.LOADING : REGISTER_PAGE.REGISTER_CHECK_FORM
    // isOAuth ? REGISTER_PAGE.LOADING : REGISTER_PAGE.REGISTER_FORM
  );

  useEffect(() => {
    if (isOAuth) {
      loadOAuthContext();
    }
  }, []);

  const loadOAuthContext = async () => {
    try {
      const response = await client.oauthContext();
      log.debug('LOAD_OAUTH_CONTEXT_SUCCESS');
      const data = response.data;
      setOAuthContext(data);

      if (data.intent === 'login') {
        toast.info('회원이 아닙니다. 회원 가입을 진행합니다.');
        setPageState(REGISTER_PAGE.REGISTER_CHECK_FORM);
      } else {
        setPageState(REGISTER_PAGE.OAUTH_COMPLETE_FORM);
      }
    } catch(e) {
      log.debug('LOAD_OAUTH_CONTEXT_FAILED', e);
      const errors = parseErrors(e.response);
      toast.error(errors[0].message);
      setPageState(REGISTER_PAGE.REGISTER_CHECK_FORM);
    }
  };

  const onSuccessRegisterCheck = () => {
    if (isOAuth) {
      setPageState(REGISTER_PAGE.OAUTH_COMPLETE_FORM);
    } else {
      setPageState(REGISTER_PAGE.REGISTER_FORM);
    }
  };

  const onSubmit = async (allow_notice) => {
    if (submitting) return;
    setSubmitting(true);
    
    try {
      if (isOAuth) {
        const { name, email, mobile } = registerData;
        const oauthCompleteData = {
          name,
          email,
          mobile,
          allow_notice,
        }
        await completeOAuth(oauthCompleteData);
      } else {
        const data = {
          ...registerData,
          allow_notice,
        };      
        await register(data);        
      }
    } finally {
      setSubmitting(false);
    }
  };

  const register = async (data) => {
    try {
      const response = await client.register(data);
      log.debug('AUTH_REGISTER_SUCCESS', response);
      toast.success('회원가입에 성공했습니다.');
      syncAuth();
      navigate('/');
    } catch (e) {
      log.debug('AUTH_REGISTER_FAILED', e);
      handleThrottle(e.response);
      setFieldError(parseFieldError(e.response));
      setPageState(REGISTER_PAGE.REGISTER_FORM);
    }
  };

  const completeOAuth = async (data) => {
    try {
      const response = await client.oauthComplete(data);
      log.debug('OAUTH_COMPLETE_SUCCESS', response);
      toast.success('정상적으로 가입되었습니다.');
      syncAuth();
      navigate('/');
    } catch(e) {
      log.debug('OAUTH_COMPLETE_FAILED', e);
      const throttled = handleThrottle(e.response);
      if (!throttled) {
        const errors = parseErrors(e.response);
        toast.error(errors[0].message);
      };
      setPageState(REGISTER_PAGE.REGISTER_FORM);
    }
  };
  
  return (
    <div className="RegisterPage">
      {pageState === REGISTER_PAGE.REGISTER_CHECK_FORM &&
        <RegisterCheckModal
          onSuccess={onSuccessRegisterCheck}
        />
      }
      {pageState === REGISTER_PAGE.REGISTER_FORM && 
        <RegisterModal
          registerData={registerData}
          fieldError={fieldError}
          setFieldError={setFieldError}
          setRegisterData={setRegisterData}
          onSuccess={() => setPageState(REGISTER_PAGE.CONSENT_FORM)}
      />}

      {pageState === REGISTER_PAGE.OAUTH_COMPLETE_FORM && 
        <OAuthCompleteModal
          pendingData={oauthContext}
          registerData={registerData}
          setRegisterData={setRegisterData}
          onSuccess={() => setPageState(REGISTER_PAGE.CONSENT_FORM)}
          onFailure={() => setPageState(REGISTER_PAGE.REGISTER_FORM)}
      />}
      
      {pageState === REGISTER_PAGE.CONSENT_FORM &&
        <ConsentModal
          submitting={submitting}
          onSubmit={onSubmit}
          onCancel={() => setPageState(REGISTER_PAGE.REGISTER_FORM)}
        />
      }
    </div>
  );
};